import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid } from "recharts";
import { ArrowLeft, MousePointerClick, ShoppingBag, TrendingUp, DollarSign, ExternalLink } from "lucide-react";
import { getCreatorDetail } from "@/lib/admin-creator-detail.functions";
import { resolveImage } from "@/lib/image-map";

export const Route = createFileRoute("/_authenticated/admin/creators/$id")({
  head: () => ({
    meta: [
      { title: "Creator detail — Admin" },
      { name: "description", content: "Per-creator KPIs, activity chart and product-level performance breakdown." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: CreatorDetailPage,
});

const WINDOWS = [
  { d: 7, label: "7d" },
  { d: 30, label: "30d" },
  { d: 90, label: "90d" },
] as const;

function fmtCurrency(n: number, ccy = "GBP") {
  try { return new Intl.NumberFormat("en-GB", { style: "currency", currency: ccy }).format(n); }
  catch { return `£${n.toFixed(2)}`; }
}
function fmtPct(n: number) {
  return `${(n * 100).toFixed(2)}%`;
}

function CreatorDetailPage() {
  const { id } = Route.useParams();
  const [windowDays, setWindowDays] = useState<number>(30);
  const fetchDetail = useServerFn(getCreatorDetail);

  const { data, isLoading, error } = useQuery({
    queryKey: ["admin-creator-detail", id, windowDays],
    queryFn: async () => fetchDetail({ data: { creatorId: id, windowDays } }),
  });

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between gap-4">
        <Link to="/admin/creators" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-3 w-3" /> Back to creators
        </Link>
        <div className="flex gap-1">
          {WINDOWS.map((w) => (
            <button
              key={w.d}
              onClick={() => setWindowDays(w.d)}
              className={`border px-3 py-1 text-xs uppercase tracking-widest ${
                windowDays === w.d ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{(error as Error).message}</p>}

      {data && (
        <>
          <header className="flex flex-wrap items-center gap-4 border border-border bg-card p-4 sm:p-5">
            <img src={resolveImage(data.creator.avatar_url)} alt={data.creator.name} className="h-16 w-16 rounded-full object-cover" />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-serif text-2xl">{data.creator.name}</h2>
                <span className="text-sm text-muted-foreground">@{data.creator.handle}</span>
                <span className="bg-muted px-2 py-0.5 text-[10px] uppercase tracking-widest">{data.creator.status}</span>
              </div>
              {data.creator.bio && <p className="mt-1 text-sm text-muted-foreground">{data.creator.bio}</p>}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                {data.creator.followers && <span>{data.creator.followers} followers</span>}
                {(data.creator.categories ?? []).length > 0 && <span>{data.creator.categories!.join(", ")}</span>}
                {data.creator.website && (
                  <a href={data.creator.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline">
                    Site <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
            <Link
              to="/creator/$handle"
              params={{ handle: data.creator.handle }}
              className="border border-border px-3 py-2 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background"
            >
              View storefront
            </Link>
          </header>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Kpi label="Clicks" value={data.totals.clicks.toLocaleString()} icon={<MousePointerClick className="h-4 w-4" />} />
            <Kpi label="Orders" value={data.totals.orders.toLocaleString()} icon={<ShoppingBag className="h-4 w-4" />} />
            <Kpi label="Conversion" value={fmtPct(data.totals.conversionRate)} icon={<TrendingUp className="h-4 w-4" />} />
            <Kpi
              label="Revenue"
              value={fmtCurrency(data.totals.revenue, data.totals.currency)}
              sub={`Commission ${fmtCurrency(data.totals.commission, data.totals.currency)}`}
              icon={<DollarSign className="h-4 w-4" />}
            />
          </div>

          <section className="border border-border bg-card p-5">
            <h3 className="font-serif text-lg">Activity · last {data.windowDays} days</h3>
            <div className="mt-4 h-56 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.series}>
                  <defs>
                    <linearGradient id="clicksFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--foreground))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--foreground))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", fontSize: 12 }}
                    formatter={(v: any, k: any) =>
                      k === "revenue" ? [fmtCurrency(Number(v), data.totals.currency), "Revenue"] : [Number(v).toLocaleString(), k]
                    }
                  />
                  <Area type="monotone" dataKey="clicks" stroke="hsl(var(--foreground))" fill="url(#clicksFill)" strokeWidth={2} />
                  <Area type="monotone" dataKey="orders" stroke="hsl(var(--primary))" fill="transparent" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section>
            <div className="flex items-baseline justify-between">
              <h3 className="font-serif text-lg">Revenue by product</h3>
              <span className="text-xs uppercase tracking-widest text-muted-foreground">{data.products.length} products</span>
            </div>
            <div className="mt-3 overflow-x-auto border border-border -mx-4 sm:mx-0">
              <table className="w-full min-w-[720px] text-sm">
                <thead className="bg-muted/30 text-left text-xs uppercase tracking-widest text-muted-foreground">
                  <tr>
                    <th className="px-4 py-3">Product</th>
                    <th className="px-4 py-3 text-right">Clicks</th>
                    <th className="px-4 py-3 text-right">Orders</th>
                    <th className="px-4 py-3 text-right">CVR</th>
                    <th className="px-4 py-3 text-right">Revenue</th>
                    <th className="px-4 py-3 text-right">Commission</th>
                  </tr>
                </thead>
                <tbody>
                  {data.products.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">No products curated yet.</td></tr>
                  )}
                  {data.products.map((p) => {
                    const cvr = p.clicks > 0 ? p.orders / p.clicks : 0;
                    return (
                      <tr key={p.productId} className="border-t border-border align-middle">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <img src={resolveImage(p.image_key)} alt={p.title} className="h-10 w-10 rounded object-cover" />
                            <div className="min-w-0">
                              <div className="truncate font-medium">{p.title}</div>
                              <div className="text-xs text-muted-foreground">{p.brand}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums">{p.clicks.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{p.orders.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{fmtPct(cvr)}</td>
                        <td className="px-4 py-3 text-right tabular-nums">{fmtCurrency(p.revenue, p.currency)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">{fmtCurrency(p.commission, p.currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, sub, icon }: { label: string; value: string; sub?: string; icon: React.ReactNode }) {
  return (
    <div className="border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground">
        {icon} {label}
      </div>
      <div className="mt-2 font-serif text-2xl">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}
